import { Dialog, Transition } from '@headlessui/react';
import {
  CogIcon,
  MenuAlt2Icon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/outline';
import { LogoutIcon, XIcon } from '@heroicons/react/solid';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { Fragment, memo, useState } from 'react';
import * as React from 'react';

import clsxm from '@/lib/clsxm';
import { useDashboardChromePresence } from '@/lib/siteChatVisibility';
import { useFavoriteTools } from '@/hooks/useFavoriteTools';

import { getPresetToolHref, presets } from '@/constants/presets';
import { logout } from '@/utils/auth';
import { useUser } from '@/utils/useUser';

import { navigation } from './DashboardLayout';
import ToolsFlyout from './ToolsFlyout';
import { Tooltip } from '../atoms/Tooltip';

function NarrowDashboardLayout({
  children,
  sidebarVisible = true,
  showMobileMenu = false,
  mobileMenuTone = 'default',
}: {
  children: React.ReactNode;
  sidebarVisible?: boolean;
  showMobileMenu?: boolean;
  /** 'dark' for pages that are always dark (the video editor gallery). */
  mobileMenuTone?: 'default' | 'dark';
}) {
  // Tells the site-wide support bubble it is inside the product (see
  // SiteChatWidget); BotDashboardLayout wraps this component, so it's covered.
  useDashboardChromePresence();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsFlyoutOpen, setToolsFlyoutOpen] = useState(false);
  const [toolsFlyoutPos, setToolsFlyoutPos] = useState({ top: 0, left: 0 });
  const toolsItemRef = React.useRef<HTMLDivElement>(null);
  const flyoutCloseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const { t } = useLocale('dashboard');
  const router = useRouter();
  const isChat = router.pathname === '/chat';
  const { user } = useUser();
  const { favoriteTools } = useFavoriteTools();

  // Create navigation items from favorite presets (top 10)
  const favoriteNavigationItems = React.useMemo(() => {
    const navigationSlugs = navigation.map((item) => item.slug);

    const items: Array<{
      name: string;
      href: string;
      icon: any;
      slug: string;
    }> = [];

    favoriteTools
      .filter((slug) => !navigationSlugs.includes(slug))
      .slice(0, 10)
      .forEach((slug) => {
        const preset = presets.find((p) => p.slug === slug);
        if (preset) {
          items.push({
            name: preset.title,
            href: getPresetToolHref(preset),
            icon: preset.icon,
            slug: preset.slug,
          });
        }
      });

    return items;
  }, [favoriteTools]);

  // Merge navigation with favorite items
  const allNavigation = React.useMemo(() => {
    const favouritesIndex = navigation.findIndex(
      (item) => item.slug === 'favourites',
    );

    if (favouritesIndex === -1) {
      const toolsStartIndex =
        navigation.findIndex((item) => item.slug === 'voices') + 1;
      return [
        ...navigation.slice(0, toolsStartIndex),
        ...favoriteNavigationItems,
        ...navigation.slice(toolsStartIndex),
      ];
    }

    return [
      ...navigation.slice(0, favouritesIndex + 1),
      ...favoriteNavigationItems,
      ...navigation.slice(favouritesIndex + 1),
    ];
  }, [favoriteNavigationItems]);

  // Filter navigation items based on pinned status
  const visibleNavigation = React.useMemo(() => {
    return allNavigation.filter((item) => {
      // @ts-ignore - cannotUnpin exists on items now
      if (item.cannotUnpin) {
        return true;
      }

      if (!user) {
        return item.name !== 'Home';
      }

      // Always show favorite items
      // @ts-ignore - slug exists on items now
      return favoriteTools.includes(item.slug);
    });
  }, [allNavigation, user, favoriteTools]);

  return (
    <>
      {/*
        This example requires updating your template:

        ```
        <html class="h-full bg-gray-50">
        <body class="h-full overflow-hidden">
        ```
      */}
      {/* translate='no': Chrome auto-translate breaks React reconciliation
          (see DashboardLayout) — the app ships 13 locales natively. */}
      <div
        translate='no'
        className='notranslate flex h-dvh overflow-hidden sm:h-screen'
      >
        <Transition.Root show={mobileMenuOpen} as={Fragment}>
          <Dialog
            as='div'
            className='relative z-50 lg:hidden'
            onClose={setMobileMenuOpen}
          >
            <Transition.Child
              as={Fragment}
              enter='transition-opacity ease-linear duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition-opacity ease-linear duration-300'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <div className='fixed inset-0 bg-gray-600 bg-opacity-75' />
            </Transition.Child>

            <div className='fixed inset-0 z-40 flex'>
              <Transition.Child
                as={Fragment}
                enter='transition ease-in-out duration-300 transform'
                enterFrom='-translate-x-full'
                enterTo='translate-x-0'
                leave='transition ease-in-out duration-300 transform'
                leaveFrom='translate-x-0'
                leaveTo='-translate-x-full'
              >
                <Dialog.Panel className='relative flex w-full max-w-xs flex-1 flex-col bg-white focus:outline-none'>
                  <Transition.Child
                    as={Fragment}
                    enter='ease-in-out duration-300'
                    enterFrom='opacity-0'
                    enterTo='opacity-100'
                    leave='ease-in-out duration-300'
                    leaveFrom='opacity-100'
                    leaveTo='opacity-0'
                  >
                    <div className='absolute right-0 top-0 -mr-12 pt-4'>
                      <button
                        type='button'
                        className='ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <span className='sr-only'>Close sidebar</span>
                        <XIcon
                          className='h-6 w-6 text-white'
                          aria-hidden='true'
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  <div className='flex h-full flex-col overflow-y-auto pb-4 pt-5'>
                    <div className='flex flex-shrink-0 items-center px-4'>
                      <img
                        className='h-8 w-auto'
                        src='/images/marky.png'
                        alt='Easy-Peasy.AI'
                      />
                    </div>
                    <nav aria-label='Sidebar' className='mt-5 flex-1'>
                      <div className='space-y-1 px-2'>
                        {visibleNavigation.map((item) => (
                          <a
                            key={item.name}
                            href={item.href}
                            className='group flex items-center rounded-md p-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          >
                            <item.icon
                              className='mr-4 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                              aria-hidden='true'
                            />
                            {t(item.name)}
                          </a>
                        ))}
                      </div>
                      <div className='mt-auto space-y-1 pt-10'>
                        <Link
                          href='/settings'
                          className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600'
                        >
                          <CogIcon
                            className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                          {t('Settings')}
                        </Link>
                        <Link
                          href='/support'
                          className='group flex cursor-pointer items-center border-l-4 border-transparent px-3 py-2 text-sm font-medium text-gray-600'
                        >
                          <QuestionMarkCircleIcon
                            className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                          {t('Help & Support')}
                        </Link>
                        <a
                          onClick={logout}
                          className='group flex items-center border-l-4 border-transparent px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        >
                          <LogoutIcon
                            className='mr-3 h-6 w-6 text-gray-400 group-hover:text-gray-500'
                            aria-hidden='true'
                          />
                          {t('Logout')}
                        </a>
                      </div>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
              <div className='w-14 flex-shrink-0' aria-hidden='true'>
                {/* Force sidebar to shrink to fit close icon */}
              </div>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        {!isChat && (
          <div className='hidden lg:flex lg:flex-shrink-0'>
            <div
              className={clsxm(
                sidebarVisible ? 'w-20 translate-x-0' : 'w-0 -translate-x-20',
                'relative flex transform flex-col transition-all duration-300  ease-in-out',
              )}
            >
              <div className='flex min-h-0 flex-1 flex-col overflow-y-auto bg-primary-600'>
                <div className='flex-1'>
                  <div className='flex items-center justify-center bg-primary-600 py-4'>
                    <Link href='/dashboard'>
                      <img
                        className='h-8 w-auto'
                        src='/images/marky-chat.png'
                        alt='Easy-Peasy.AI'
                      />
                    </Link>
                  </div>
                  <nav
                    aria-label='Sidebar'
                    className='flex flex-col items-center space-y-1 py-6'
                  >
                    {visibleNavigation.map((item) => {
                      // @ts-ignore - slug exists on items now
                      const isToolsItem = item.slug === 'tools';

                      return (
                        <div
                          key={item.name}
                          ref={isToolsItem ? toolsItemRef : null}
                          onMouseEnter={() => {
                            if (isToolsItem) {
                              if (flyoutCloseTimeoutRef.current) {
                                clearTimeout(flyoutCloseTimeoutRef.current);
                                flyoutCloseTimeoutRef.current = null;
                              }
                              if (toolsItemRef.current) {
                                const rect =
                                  toolsItemRef.current.getBoundingClientRect();
                                setToolsFlyoutPos({
                                  top: rect.top,
                                  left: rect.right,
                                });
                              }
                              setToolsFlyoutOpen(true);
                            }
                          }}
                          onMouseLeave={() => {
                            if (isToolsItem) {
                              flyoutCloseTimeoutRef.current = setTimeout(() => {
                                setToolsFlyoutOpen(false);
                              }, 300);
                            }
                          }}
                        >
                          <Link
                            href={item.href}
                            className='flex items-center rounded-lg p-3 text-primary-200 hover:bg-primary-700'
                          >
                            <Tooltip
                              message={t(item.name)}
                              className=' w-6 text-center'
                            >
                              <kbd>
                                <item.icon
                                  className='h-6 w-6'
                                  aria-hidden='true'
                                  title={t(item.name)}
                                />
                              </kbd>
                            </Tooltip>
                            <span className='sr-only'>{t(item.name)}</span>
                          </Link>
                        </div>
                      );
                    })}
                  </nav>
                </div>
                <div className='flex flex-col items-center space-y-1 py-6 pb-5'>
                  <Link
                    href='/settings'
                    className='flex items-center rounded-lg p-3 text-primary-200 hover:bg-primary-700'
                  >
                    <Tooltip
                      message={t('Settings')}
                      className='w-6 truncate text-center'
                    >
                      <kbd>
                        <CogIcon className='h-6 w-6' aria-hidden='true' />
                      </kbd>
                    </Tooltip>
                  </Link>
                  <Link
                    href='/support'
                    className='flex items-center rounded-lg p-3 text-primary-200 hover:bg-primary-700'
                  >
                    <Tooltip
                      message={t('Help')}
                      className='w-6 truncate text-center'
                    >
                      <kbd>
                        <QuestionMarkCircleIcon
                          className='h-6 w-6'
                          aria-hidden='true'
                        />
                      </kbd>
                    </Tooltip>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='flex min-w-0 flex-1 flex-col overflow-hidden'>
          {/* Mobile top navigation */}
          {showMobileMenu && mobileMenuTone === 'dark' && (
            <div className='absolute left-3 top-[1.4rem] z-50 lg:hidden lg:top-3'>
              <button
                type='button'
                className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/[0.06] text-zinc-300 ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.1] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400'
                onClick={() => setMobileMenuOpen(true)}
              >
                <span className='sr-only'>Open sidebar</span>
                <MenuAlt2Icon className='h-5 w-5' aria-hidden='true' />
              </button>
            </div>
          )}
          {showMobileMenu && mobileMenuTone === 'default' && (
            <div className='absolute z-50 lg:hidden'>
              <div className='mt-1 flex items-center justify-between bg-gray-100 px-4 dark:bg-slate-900'>
                <div>
                  <button
                    type='button'
                    className='inline-flex items-center justify-center rounded-md text-gray-500 focus:ring-2 focus:ring-inset focus:ring-primary-500'
                    onClick={() => setMobileMenuOpen(true)}
                  >
                    <span className='sr-only'>Open sidebar</span>
                    <MenuAlt2Icon className='h-6 w-6' aria-hidden='true' />
                  </button>
                </div>
              </div>
            </div>
          )}

          {children}
        </div>
      </div>

      {/* Tools Flyout Menu - fixed positioning to escape overflow clipping */}
      {toolsFlyoutOpen && (
        <>
          {/* Invisible bridge to prevent flyout from closing when moving mouse */}
          <div
            className='fixed z-[60] h-12 w-4'
            style={{
              top: `${toolsFlyoutPos.top}px`,
              left: `${toolsFlyoutPos.left}px`,
            }}
            onMouseEnter={() => {
              if (flyoutCloseTimeoutRef.current) {
                clearTimeout(flyoutCloseTimeoutRef.current);
                flyoutCloseTimeoutRef.current = null;
              }
            }}
          />

          <div
            className='fixed z-[60]'
            style={{
              top: `${toolsFlyoutPos.top}px`,
              left: `${toolsFlyoutPos.left}px`,
            }}
            onMouseEnter={() => {
              if (flyoutCloseTimeoutRef.current) {
                clearTimeout(flyoutCloseTimeoutRef.current);
                flyoutCloseTimeoutRef.current = null;
              }
              setToolsFlyoutOpen(true);
            }}
            onMouseLeave={() => {
              setToolsFlyoutOpen(false);
            }}
          >
            <ToolsFlyout
              isOpen={toolsFlyoutOpen}
              onMouseEnter={() => {
                if (flyoutCloseTimeoutRef.current) {
                  clearTimeout(flyoutCloseTimeoutRef.current);
                  flyoutCloseTimeoutRef.current = null;
                }
              }}
              onMouseLeave={() => {}}
            />
          </div>
        </>
      )}
    </>
  );
}

//memoized export
export default memo(NarrowDashboardLayout);
